import { Subscription } from 'rxjs';

/**
 * TEARDOWN MUST NOT BE ABLE TO BREAK ROUTING.
 *
 * The bug that produced this file, from prod2's console:
 *
 *     ERROR TypeError: this.tickPositionSubscription.unsubscribe is not a function
 *         at c.ngOnDestroy
 *         at ... Lg.destroy -> Fv.destroy -> v.deactivate
 *         at us.deactivateRouteAndOutlet
 *
 * Read the bottom of that stack: ngOnDestroy threw during ROUTE DEACTIVATION.
 * When the router is tearing down the outgoing component and that teardown
 * raises, THE NAVIGATION FAILS — so the user stays where they were. That single
 * mechanism produced a family of symptoms that looked unrelated for days:
 *
 *   - clicking a tab appeared to "take you back to temperature" (the navigation
 *     aborted, so you never left);
 *   - a second click sometimes worked, because the first attempt had already
 *     changed the state that threw;
 *   - charts were blank, because the outgoing component's ngOnDestroy had already
 *     DELETED its SciChart surfaces before it threw — leaving a live route with
 *     dead surfaces;
 *   - and a refresh always fixed it, because a full page load has no route
 *     deactivation to fail.
 *
 * THE FIELD WAS NEVER A SUBSCRIPTION. Every one of these components declares its
 * subscription fields as `Subscription = {} as Subscription` — a lie to the type
 * checker that is true right up until something calls a method on it. The shared
 * tick work (859d5e3) moved the tick to ChartTickService.register() and left
 * `tickPositionSubscription` behind, never assigned, still unsubscribed in
 * ngOnDestroy.
 *
 * THAT PATTERN IS A LANDMINE PER FIELD, not one bug. A component destroyed before
 * its async initialisation has assigned these fields hits exactly the same throw,
 * which is why this was intermittent and why a fast tab click made it likelier.
 * So the fix is both halves: remove the dead field, AND make teardown incapable
 * of taking routing down with it.
 */

/**
 * Unsubscribe if there is anything real to unsubscribe, and never throw.
 *
 * Deliberately tolerant of the `{} as Subscription` placeholder: that object is
 * not a subscription and has nothing to release, so there is nothing to report.
 * A REAL failure inside a genuine unsubscribe is logged, because that is a defect
 * worth seeing — it just must not reach the router.
 */
export function closeSubscription(label: string, candidate: unknown): void {
  const sub = candidate as Partial<Subscription> | null | undefined;
  if (!sub || typeof sub.unsubscribe !== 'function') {
    return;
  }
  try {
    sub.unsubscribe();
  } catch (err) {
    console.error(`Teardown: unsubscribing ${label} failed`, err);
  }
}

/**
 * Run a teardown body so that a throw inside it is logged rather than propagated.
 *
 * The router calls ngOnDestroy while deactivating a route; anything that escapes
 * aborts the navigation. Losing a chart surface or a timer is a leak. Losing the
 * ability to change tabs is the application not working, and the second is a
 * disproportionate price for the first — so teardown fails quietly and loudly in
 * the log, never in the router.
 *
 * NOT a substitute for correct teardown: it is the backstop that keeps the next
 * mistake of this shape from costing navigation again.
 */
export function runTeardown(label: string, body: () => void): void {
  try {
    body();
  } catch (err) {
    console.error(`Teardown: ${label} failed`, err);
  }
}
